import { useRef, useCallback } from 'react';
import { query, collection, Query, QuerySnapshot } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import { auth } from '../constants/firebaseModular';
import { Chart } from '@usacsmideo/types';
import { formatChart } from '../data/formatChart';
import { deepEqual } from '../utilities/deepEqual';
import { useFirestoreListener } from './useFirestoreListener';

function useChart(
  patientId: string,
  {
    enabled = true,
    withheldByPolicy = false,
  }: { enabled?: boolean; withheldByPolicy?: boolean } = {},
) {
  // Defense-in-depth: never start a listener for a patient that doesn't match the
  // authenticated user. The $patientId layout route should prevent this, but if a
  // child component somehow mounts during sign-out, this stops the listener cold.
  const uidMatch = auth.currentUser?.uid === patientId;
  const lastRetObjRef = useRef<Chart | null>(null);

  const transform = useCallback((querySnapshot: QuerySnapshot): Chart | undefined => {
    const retObj = {} as Chart;
    querySnapshot.docs.forEach((doc) => {
      const data = formatChart(doc);
      const key = Object.keys(data)[0] as keyof Chart;
      const value = data[key];
      retObj[key] = value;
    });

    if (deepEqual(lastRetObjRef.current, retObj)) {
      return undefined;
    }
    lastRetObjRef.current = retObj;
    return retObj;
  }, []);

  return useFirestoreListener<Query, Chart>({
    target: query(collection(db, 'users', patientId, 'Chart')),
    queryKey: ['chart', patientId],
    label: 'chart',
    enabled: enabled && uidMatch,
    // CU-86ak77fuv B1a — when the MFA gate withholds the chart, consumers must
    // be able to tell "not permitted" from "still loading". See
    // `useFirestoreListener`'s `withheldByPolicy`.
    withheldByPolicy,
    transform,
  });
}

export default useChart;
